import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import adminService from "services/adminService";
import orgDataService from "services/orgDataService";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";
import { toast } from "react-toastify";

interface Hub {
  hubId: number;
  hubName: string;
}

interface DispatchQueueConfig {
  dispatchQueueConfigId: number;
  name: string;
  active: boolean;
  hubs: Hub[];
}

const DispatchQueueEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [queue, setQueue] = useState<DispatchQueueConfig | null>(null);
  const [allHubs, setAllHubs] = useState<Hub[]>([]);
  const [selectedHubId, setSelectedHubId] = useState<number | null>(null);
  const [availableHubs, setAvailableHubs] = useState<Hub[]>([]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [queueData, hubsData] = await Promise.all([
        adminService.getDispatchQueue(Number(id)),
        orgDataService.getAllHubs(),
      ]);
      setQueue(queueData);
      const hubs = hubsData.map(h => ({ hubId: h.id, hubName: h.name }));
      setAllHubs(hubs);
      updateAvailableHubs(queueData, hubs);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const updateAvailableHubs = (queueData: DispatchQueueConfig, hubsData: Hub[]) => {
    const assignedHubIds = queueData.hubs?.map((h) => h.hubId) || [];
    const available = hubsData.filter((h) => !assignedHubIds.includes(h.hubId));
    setAvailableHubs(available);
  };

  const addHub = async () => {
    if (!selectedHubId || !queue) return;
    try {
      await adminService.addDispatchQueueHub(queue.dispatchQueueConfigId, selectedHubId);
      setSelectedHubId(null);
      await loadData();
      toast.success("Hub added successfully");
    } catch (error) {
      toast.error("Failed to add hub");
    }
  };

  const removeHub = async (hubId: number) => {
    if (!queue) return;
    try {
      await adminService.removeDispatchQueueHub(queue.dispatchQueueConfigId, hubId);
      await loadData();
      toast.success("Hub removed successfully");
    } catch (error) {
      toast.error("Failed to remove hub");
    }
  };

  if (!queue) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Edit Dispatch Queue: {queue.name}</Typography>
        <MDButton variant="outlined" onClick={() => navigate("/admin/dispatch-queues")}>
          Back to List
        </MDButton>
      </Box>

      <Card sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Hub
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Hub</InputLabel>
            <Select
              value={selectedHubId || ""}
              onChange={(e) => setSelectedHubId(Number(e.target.value))}
              label="Select Hub"
            >
              {availableHubs.map((hub) => (
                <MenuItem key={hub.hubId} value={hub.hubId}>
                  {hub.hubName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDButton
            variant="contained"
            color="primary"
            onClick={addHub}
            disabled={!selectedHubId}
          >
            Add Hub
          </MDButton>
        </Box>
      </Card>

      <Card>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Assigned Hubs
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Hub Name</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queue.hubs?.length > 0 ? (
                queue.hubs.map((hub) => (
                  <TableRow key={hub.hubId}>
                    <TableCell>{hub.hubName}</TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => removeHub(hub.hubId)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No hubs assigned
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

export default DispatchQueueEdit;
